import React, { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { ReactComponent as DnaVisitTypeIcon } from 'assets/icons/appointmentsIcons/dnaVisitTypeIconTable.svg';

import { ReactComponent as Clock } from 'assets/icons/time.svg';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import { ReactComponent as Reason } from 'assets/icons/appointmentsIcons/reason-goal.svg';
import { ReactComponent as GoogleCalendarIcon } from 'assets/icons/google-calendar.svg';
import { ReactComponent as OutlookIcon } from 'assets/icons/outlook-icon.svg';

import OptionsModal, {
  IOptionProps,
} from 'components/optionsModal/OptionsModal';
import ButtonComponent from 'components/button/buttonComponent';
import IconRow from './IconRow';
import { getPeriodString } from './StartTimeAndTypeappointment';
import { AppointmentHasuraStatus } from '../../const/appointment';
import { UpcomingAppoinment } from '../../interfaces';
import { useAppointmentCardHelper } from './helper/useAppointmentCardHelper';
import { getUserBrowserTimeZone } from '../../../../utilities/functions';
import { getAppointmentDate } from '../../../../utilities/dateHelper';
import { mapUpcomingDnaAppointmentToCalendarParams } from '../../../../utilities/mapAppointmentToCalendarParams';
import { sanitize } from '../../../../utilities/sanitizer';
import { useNavigate } from 'react-router-dom';
import { SCHEDULE_DNA_VISIT } from 'utilities/routes';
import { useAddToCalendar } from '../../../../hooks/useAddToCalendar';

interface IUpcomingAppointmentCard extends UpcomingAppoinment {
  appointmentStatus: AppointmentHasuraStatus;
  handleNameClick: () => void;
  options: IOptionProps[];
  handleDetailsClick?: () => void;
}

const UpcomingAppointmentDnaCardMobile: React.FC<IUpcomingAppointmentCard> = (
  appointment,
) => {
  const {
    appointmentStatus,
    appointmentUrl,
    date,
    end,
    status,
    name,
    appointmentType,
    appointmentId,
    providerId,
    patientId,
  } = appointment;
  const navigate = useNavigate();
  const { addToGoogleCalendar, addToOutlookCalendar } = useAddToCalendar();

  const {
    loading,
    locale,
    label,
    iconClassNames,
    Icon,
    isNotPaid,
    isJoinable,
    statusClassName,
    notificationClassNames,
    notification,
    opened,
    setOpened,
    hasPastTimeCancelAppointment,
    handleRetryPayment,
  } = useAppointmentCardHelper({
    date,
    appointmentStatus,
    end,
    status,
    appointmentId,
    appointmentType,
    providerId,
    patientId,
    name,
  });

  const options = useMemo(
    () => [
      {
        Icon: GoogleCalendarIcon,
        label: locale?.addToGoogleCalendar || 'Add to Google calendar',
        onClick: () =>
          addToGoogleCalendar(
            mapUpcomingDnaAppointmentToCalendarParams(appointment),
          ),
      },
      {
        Icon: OutlookIcon,
        label: locale?.addToOutlookCalendar || 'Add to Outlook calendar',
        onClick: () =>
          addToOutlookCalendar(
            mapUpcomingDnaAppointmentToCalendarParams(appointment),
          ),
      },
    ],
    [addToGoogleCalendar, addToOutlookCalendar, locale, appointment],
  );

  const handleJoin = () =>
    window.open(sanitize(appointmentUrl ?? ''), '_blank');
  const handleDetailsClick = () => navigate(SCHEDULE_DNA_VISIT);

  const tz = getUserBrowserTimeZone();

  return (
    <div className="flex flex-col w-full desktop:hidden">
      <div className="p-5 border border-transparent border-b-gray-opacity-15 bg-neutral-100 rounded-tr-xl rounded-tl-xl relative">
        <div className={twMerge('capitalize w-max mb-2.5', statusClassName)}>
          {label || status}
        </div>
        <div className="flex mb-2">
          <span>
            {Icon && notification && <Icon className={iconClassNames} />}
          </span>
          <span className={notificationClassNames}>{notification}</span>
        </div>
        {!hasPastTimeCancelAppointment && (
          <div className="absolute top-2 right-5">
            <OptionsModal
              opened={opened}
              setOpened={setOpened}
              options={options}
              className="!bg-transparent !border-transparent -mr-5"
              optionsMenuClassName="rounded-md w-[16rem] !border-transparent -left-[14rem]"
              optionsClassName="uppercase text-clc-blue text-sm font-bold"
              btnTitleClassName="hidden"
            />
          </div>
        )}
        <div className="flex">
          <DnaVisitTypeIcon className="w-6 min-w-[1.5rem] mr-1" />
          <span className="text-base font-semibold text-neutral-800">
            DNA appointment with Dr. {name}
          </span>
        </div>
      </div>
      <div className="p-5 pt-2.5 border-b-gray-opacity-15 bg-neutral-100 flex flex-col gap-2 rounded-br-xl rounded-bl-xl">
        <IconRow
          icon={<CalendarIcon className="fill-neutral-800" />}
          text={getAppointmentDate(date)}
        />
        <IconRow
          icon={<Clock className="fill-neutral-800" />}
          text={`${getPeriodString(date, end)} ${tz}`}
          testId={`start-datetime-${date}`}
        />
        <IconRow
          icon={<Reason className="min-w-[1.25rem]" />}
          text="DNA results overview"
        />

        {handleDetailsClick && (
          <ButtonComponent
            type="underline"
            className="!text-sm rounded w-full !px-3 ml-auto block"
            desktopFullWidth
            testID={'legal-doc-edit-appointment'}
            onClick={handleDetailsClick}
          >
            {locale?.details || 'Details'}
          </ButtonComponent>
        )}
        {isJoinable && (
          <ButtonComponent
            className="!text-sm rounded desktop:max-w-[6.75rem] !px-3 desktop:ml-auto block"
            desktopFullWidth
            testID={'join-appointment-button'}
            onClick={handleJoin}
          >
            {locale?.joinNow}
          </ButtonComponent>
        )}

        {isNotPaid && (
          <ButtonComponent
            className="!text-sm rounded desktop:!w-auto !px-3 ml-auto block"
            desktopFullWidth
            disabled={loading}
            testID={'retry-payment'}
            onClick={handleRetryPayment}
          >
            Retry payment
          </ButtonComponent>
        )}
      </div>
    </div>
  );
};

export default UpcomingAppointmentDnaCardMobile;
